import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListAllowLists } from 'src/features/allowList/api/useListAllowLists';
import CellBlackListActions from 'src/features/allowList/components/CellBlackListActions';
import CreateBlackListFormModal from 'src/features/allowList/components/forms/blackList/CreateBlackListFormModal';
import { allowListMessages } from 'src/features/allowList/utils/allowListMessages';
import { ALLOW_LIST_TYPE, AllowList as AllowListType } from 'src/features/allowList/utils/types';
import { PERMISSION } from 'src/features/auth/types';
import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { useListZones } from 'src/features/zone/api/useListZones';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { Button } from 'src/registry/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'search' };

const columnHelper = createColumnHelper<AllowListType>();
export default function BlackLists() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const manageAllowLists = useHasPermissions([PERMISSION.MANAGE_ALLOW_LISTS]);
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
            type: ALLOW_LIST_TYPE.BLACK,
            parkingId,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, queryPaginationOptions, parkingId],
    );

    const { data, isLoading } = useListAllowLists(payload);
    const { data: zonesData } = useListZones({ parkingId });
    const { data: gatewaysData } = useListGateways({ parkingId });

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(allowListMessages.blackLists));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('description', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(generalMessages.description),
            },
        }),
        columnHelper.accessor('zones', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const zoneIds = ctx.row.original.zones;
                const zoneNames = zoneIds.map(
                    (value) =>
                        zonesData?.results
                            .find((option) => option.id === value)
                            ?.translations.find((translation) => translation.language === locale)?.name,
                );
                const zonesCount = zoneIds.length;

                const firstTwoZones = zoneNames?.slice(0, 2).join(', ');

                const remainingZones = zoneNames.slice(2);

                return (
                    <div>
                        {zonesCount <= 3 ? (
                            <div className="font-medium whitespace-break-spaces">
                                {zonesCount ? zoneNames.join(', ') : '-'}
                            </div>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="cursor-pointer p-0 hover:bg-transparent hover:text-inherit hover:shadow-none"
                                    >
                                        <div>{firstTwoZones}</div>
                                        <FontAwesomeIcon icon={ICON_MAP.ELLIPSIS} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-full min-w-[200px]">
                                    <DropdownMenuLabel className="text-xs font-medium text-gray-600">
                                        {formatMessage(allowListMessages.zones)}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuGroup>
                                        {remainingZones?.map((zone, index) => {
                                            return (
                                                <span
                                                    key={index}
                                                    className="relative flex items-center rounded-xs px-2 py-1.5 text-sm outline-hidden transition-colors"
                                                >
                                                    {zone}
                                                </span>
                                            );
                                        })}
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        {zonesCount > 0 && <div>{`${zonesCount} ${formatMessage(allowListMessages.total)}`}</div>}
                    </div>
                );
            },
            meta: {
                title: formatMessage(allowListMessages.zones),
            },
        }),
        columnHelper.accessor('allowed_gateways', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const gatewayIds = ctx.row.original.allowed_gateways;
                const gatewayNames = gatewayIds.map(
                    (value) =>
                        gatewaysData?.results
                            .find((option) => option.id === value)
                            ?.translations.find((translation) => translation.language === locale)?.name,
                );
                const gatewaysCount = gatewayIds.length;

                const firstTwoGateways = gatewayNames?.slice(0, 2).join(', ');

                const remainingZones = gatewayNames.slice(2);

                return (
                    <div>
                        {gatewaysCount <= 3 ? (
                            <div className="font-medium whitespace-break-spaces">
                                {gatewaysCount ? gatewayNames.join(', ') : '-'}
                            </div>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="cursor-pointer p-0 hover:bg-transparent hover:text-inherit hover:shadow-none"
                                    >
                                        <div>{firstTwoGateways}</div>
                                        <FontAwesomeIcon icon={ICON_MAP.ELLIPSIS} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-full min-w-[200px]">
                                    <DropdownMenuLabel className="text-xs font-medium text-gray-600">
                                        {formatMessage(allowListMessages.gateways)}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuGroup>
                                        {remainingZones?.map((zone, index) => {
                                            return (
                                                <span
                                                    key={index}
                                                    className="relative flex items-center rounded-xs px-2 py-1.5 text-sm outline-hidden transition-colors"
                                                >
                                                    {zone}
                                                </span>
                                            );
                                        })}
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        {gatewaysCount > 0 && <div>{`${gatewaysCount} ${formatMessage(allowListMessages.total)}`}</div>}
                    </div>
                );
            },
            meta: {
                title: formatMessage(allowListMessages.gateways),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellBlackListActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<AllowListType, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(allowListMessages.blackLists),
                entryName: formatMessage(allowListMessages.blackList).toLowerCase(),
                entryNames: formatMessage(allowListMessages.blackLists).toLowerCase(),
                placeholder: formatMessage(allowListMessages.blackList),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageAllowLists && { newEntry: CreateBlackListFormModal }),
            }}
        />
    );
}
